<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />



   <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->
   
   <!--Form Modal-->
   <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update Subscription Package' : 'Add Subscription Package'"
      title-icon="fa fa-cubes"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      @save="saveOrEditRecord"
      @sample="addSampleData"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Row 1: Package & Features -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.subscription_package"
            col="col-md-6"
            label="Subscription Package"
            icon="fa fa-cube"
            placeholder="Enter name"
            tooltip="Please enter Subscription Package."
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <TextareaField
            v-model="formInputs.features"
            col="col-md-6"
            label="Features (coma Separated)"
            icon="fa fa-list"
            rows="2"
            placeholder="e.g. Unlimited users, Priority support"
            tooltip="Please enter the features."
            required
            @input="handleInputChange"
         />
      </div>

      <!-- Row 2: Prices -->
      <div class="row g-3 mb-0">
         <MoneyField
            v-model="formInputs.subscription_price"
            col="col-md-6"
            min="0"
            currency="KES"
            label="Price Per Month (KES)"
            icon="fa fa-money"
            placeholder="For clients in Kenya"
            tooltip="Please enter the monthly price in KES (0 for a free package)."
            required
            @input="handleInputChange"
         />
         <MoneyField
            v-model="formInputs.subscription_price_usd"
            col="col-md-6"
            min="0"
            currency="USD"
            label="Price Per Month (USD)"
            icon="fa fa-money"
            placeholder="Empty = converted from KES"
            hint="For clients outside Kenya"
            @input="handleInputChange"
         />
      </div>

      <!-- Row 3: Trial days & switches -->
      <div class="row g-3 mb-0">
         <FormField
            v-model.number="formInputs.trial_days"
            id="trialDays"
            col="col-12"
            type="number"
            min="0"
            max="365"
            step="1"
            label="Free Trial Days"
            icon="fa fa-gift"
            placeholder="0 = no trial"
            hint="0 = no free trial"
            @input="handleInputChange"
         />
         <CheckField
            v-model="formInputs.is_signup_trial"
            col="col-12"
            label="Options"
            layout="horizontal"
            text="Give free trial to new sign-ups"
         >
            <CheckField v-model="formInputs.is_public" text="Show to users" />
         </CheckField>
      </div>

      <!-- Row 4: Permissions (many) -->
      <div class="row g-3 mb-0">
         <SelectField
            v-model="formInputs.permissions"
            col="col-md-12"
            name="permissions"
            label="Permissions"
            icon="fa fa-shield"
            :options="availablePermissions"
            option-label="name"
            class="multiSelect-validation-field"
            tooltip="Please select permissions."
            :submitted="submitted"
            multiple
            required
            @change="$clearValidationAlert('#recordForm')"
         />
      </div>
   </RecordFormModal>   

   
   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">

      <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('packages.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu (no Import: packages are created by hand) -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->


         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Table -->
         <div class="table-responsive">
            
          <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('subscription_package')" column="subscription_package" :table-data="tableData" @sort="orderBy">Package</SortableTh>
         <SortableTh v-if="isColumnVisible('subscription_price')" column="subscription_price" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Price / Month</SortableTh>
         <th v-if="isColumnVisible('permissions')" class="hide-sm-td">Permissions</th>
         <SortableTh v-if="isColumnVisible('features')" column="features" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Features</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>
   
   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="[
                { 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) },
                record.pinned_package ? 'pinned-row pinned-' + record.pinned_package : ''
             ]"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle'"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('subscription_package')">
               <!-- Phones: + / − and the number go in front of the package (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle'"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.subscription_package }}
               <span v-if="Number(record.is_signup_trial) === 1" class="badge bg-warning text-dark ms-1"
                     :title="`New sign-ups get this package free for ${record.trial_days} days`">
                  Sign-up trial · {{ record.trial_days }} days
               </span>
               <span v-if="Number(record.is_public) === 0" class="badge bg-secondary ms-1" title="Not shown on the clients' subscription page">
                  <i class="fa fa-eye-slash me-1"></i>Hidden from users
               </span>
               <!-- Pinned packages: small coloured label under the name (PINNED_PACKAGES in api_subscription_packages_helpers.php) -->
               <div v-if="record.pinned_package" class="mt-1">
                  <span class="badge" :class="record.pinned_package === 'developer' ? 'bg-info text-dark' : 'bg-warning text-dark'">
                     Pinned to top
                  </span>
               </div>
            </td>
            <td v-if="isColumnVisible('subscription_price')" class="hide-sm-td">
               <span v-if="Number(record.subscription_price) === 0" class="badge bg-success">Free</span>
               <template v-else>
                  <div>KES {{ formatPrice(record.subscription_price) }}</div>
                  <small class="text-muted sa-core-color">
                     USD {{ record.subscription_price_usd !== null && record.subscription_price_usd !== '' ? formatPrice(record.subscription_price_usd) : 'auto' }}
                  </small>
               </template>
            </td>
            <td v-if="isColumnVisible('permissions')" class="hide-sm-td">
               <span class="badge bg-info text-dark" :title="getPermissionNames(record.permissions)">
                  {{ getPermissionList(record.permissions).length }} permissions
               </span>
            </td>
            <td v-if="isColumnVisible('features')" class="hide-sm-td text-truncate cell-max-240" :title="record.features">
               {{ record.features }}
            </td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('packages.delete')
                     && Number(record.is_signup_trial) !== 1"
                  :can-restore="$hasPermission('packages.delete')"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                   :modal-id="'deleteCrudModal' + record.id"
                   :title="deleteModalTitle"
                   :message="deleteModalMessage"
                   @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="6" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Package Number:</strong> {{ record.subscription_package_number }}</div>
                     <div><i class="fa fa-gift"></i><strong>Free Trial Days:</strong> {{ Number(record.trial_days) || 'None' }}</div>
                     <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-building"></i><strong>Creator Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('subscription_package') }"><i class="fa fa-cube"></i><strong>Package:</strong> {{ record.subscription_package }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('subscription_price') }">
                        <i class="fa fa-money"></i><strong>Price / Month:</strong>
                        <span v-if="Number(record.subscription_price) === 0" class="badge bg-success">Free</span>
                        <template v-else>
                           KES {{ formatPrice(record.subscription_price) }}
                           <small class="text-muted sa-core-color ms-1">
                              (USD {{ record.subscription_price_usd !== null && record.subscription_price_usd !== '' ? formatPrice(record.subscription_price_usd) : 'auto' }})
                           </small>
                        </template>
                     </div>
                     <div><i class="fa fa-list"></i><strong>Features:</strong> {{ record.features }}</div>

                     <!-- Full permission list, grouped by category -->
                     <div>
                        <i class="fa fa-shield"></i><strong>Permissions ({{ getPermissionList(record.permissions).length }}):</strong>
                        <div v-for="(perms, category) in groupPermissionsByCategory(record.permissions)"
                             :key="category"
                             class="mt-2">
                           <div class="fw-semibold text-info small">{{ category }} ({{ perms.length }})</div>
                           <div class="d-flex flex-wrap gap-1 mt-1">
                              <span v-for="p in perms" :key="p.id" class="badge bg-secondary">
                                 {{ p.name || p.permission }}
                              </span>
                           </div>
                        </div>
                        <span v-if="!getPermissionList(record.permissions).length" class="text-warning">No permissions in this package.</span>
                     </div>

                     <!-- Always in details -->
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="8" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No packages yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Click Add to create the first package.'"
               icon="fa fa-cubes"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="8">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>

         </div>
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import TextareaField from "../../../components/admin/TextareaField.vue";
import MoneyField from "../../../components/admin/MoneyField.vue";
import CheckField from "../../../components/admin/CheckField.vue";
import subscriptionPackagesMixin from "./mixins/subscriptionPackagesMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'SubscriptionPackages',
   mixins: [tableMixin, subscriptionPackagesMixin], // shared table methods first, then SubscriptionPackages' own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      RecordFormModal,
      FormField,
      SelectField,
      HiddenField,
      TextareaField,
      MoneyField,
      CheckField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/subscription/api_subscription_packages.php", // used by tableMixin's fetchRecords, export
         exportFileName: "subscriptionPackagesExport", // name of the exported file
         columnsKey: "subscriptionpackages", // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         permissionOptions: [],
         pageIcon: 'fa fa-cubes', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "Subscription Package", value: "subscription_package" },
            { label: "Features", value: "features" },
         ],
      };
   },
   mounted(){
      this.fetchPermissionOptions(); // permission list for the form and the table
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location       
   },
   computed: {
    // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Subscription Packages",
              byme: "Subscription Packages By Me",
              deletedone: "Subscription Packages Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Subscription Packages";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "subscription_package", label: "Package" },
              { key: "subscription_price", label: "Price / Month" },
              { key: "permissions", label: "Permissions" },
              { key: "features", label: "Features" },
          ];
      },
      availablePermissions() {
       return this.permissionOptions.filter(option => {
         return !this.formInputs.permissions.some(selected => selected.id === option.id);
       });
     }
   },
   methods: {      
      // "1, 4,7" or [1,"4",7] -> [1, 4, 7]
      parsePermissions(value) {
         const list = typeof value === 'string' ? value.split(',')
            : Array.isArray(value) ? value
            : [];
         return list
            .map(p => Number(String(p).trim()))
            .filter(p => Number.isInteger(p) && p > 0);
      },
      // Permission objects for the IDs (Number() because the API returns ids as strings)
      getPermissionList(value) {
         const ids = this.parsePermissions(value);
         return this.permissionOptions.filter(o => ids.includes(Number(o.id)));
      },
      getPermissionNames(value) {
         return this.getPermissionList(value)
            .map(p => p.name || p.permission)
            .join(', ');
      },
      groupPermissionsByCategory(value) {
         return this.getPermissionList(value).reduce((groups, p) => {
            const key = p.permission_cat_name || 'Other';
            (groups[key] ||= []).push(p);
            return groups;
         }, {});
      },
      formatPrice(value) {
         const n = Number(value);
         return isNaN(n)
            ? value
            : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      fetchPermissionOptions(){
         let url = `${this.apiUrl}?action=fetchPermissions`;

         axios.get(url,this.$handleConfig())
            .then((response) => {
               if (response.data.status=="success") {
                  this.handleFetchPermissionsSuccess(response);
               } else {
                  this.handleAlert("starting",response.data.message,"danger","page");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","page");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","page");
                }
               
            });
      },
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            subscription_package: "",
            subscription_price: null,
            subscription_price_usd: null,
            trial_days: 0,
            is_signup_trial: false,
            is_public: true,
            permissions: [],
            selectedPermissions: [],

            //For feature modal
            features: ''
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'subscription_package',
            searchPlaceHolderText: 'Search by Subscription Package',
         };
      },
      addSampleData() {
         this.formInputs = {
            ...this.getDefaultFormInputs(),
            subscription_package: "Premium Plan",
            features: "Unlimited users, Priority support, Monthly reports",
            subscription_price: 2500,
            // v-select here holds permission objects, so take real ones from the loaded list
            permissions: this.permissionOptions.slice(0, 3),
         };

         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },

      saveOrEditRecord() {    
      if (this.isSaving) return;
      this.isSaving = true;

      try {     
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;
         const formIsValid = this.$validateForm('#recordForm');
         if (!formIsValid) { 
              this.$playErrorSound();
              this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
              this.isSaving = false;
              return;
        }
         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
              this.tableData.currentPage = 1;
         }

         //console.log(this.formInputs.permissions);
         //return;
         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response);
               } else {
                   const msg = response.data.message;
                   this.handleError("starting", Array.isArray(msg) ? msg[0] : msg, "danger", "modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
            .finally(() => {
                this.isSaving = false;
            });} catch (error) {
            this.isSaving = false;
            throw error;
        }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("subscription_package", this.formInputs.subscription_package);
         formData.append("features", this.formInputs.features);
         formData.append("subscription_price", this.formInputs.subscription_price ?? "");
         formData.append("subscription_price_usd", this.formInputs.subscription_price_usd ?? "");
         formData.append("trial_days", this.formInputs.trial_days || 0);
         formData.append("is_signup_trial", this.formInputs.is_signup_trial ? 1 : 0);
         formData.append("is_public", this.formInputs.is_public ? 1 : 0);
         
         // ✅ Convert objects → IDs
         const permissionIds = this.formInputs.permissions.map(p => p.id);

         // Send as comma-separated string (common backend pattern)
         formData.append("permissions", permissionIds.join(','));

         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.isEditing = true;
         this.$clearValidationStates('#recordForm');
         this.formInputs.id = records.id ?? '';
         this.formInputs.subscription_package = records.subscription_package ?? '';
         this.formInputs.features = records.features ?? '';
         this.formInputs.subscription_price = records.subscription_price ?? null;
         this.formInputs.subscription_price_usd = records.subscription_price_usd !== '' ? (records.subscription_price_usd ?? null) : null;
         this.formInputs.trial_days = Number(records.trial_days) || 0;
         this.formInputs.is_signup_trial = Number(records.is_signup_trial) === 1;

         this.formInputs.is_public = Number(records.is_public ?? 1) === 1;

         /*this.formInputs.permissions = typeof records.permissions === 'string'
          ? records.permissions.split(',').map(p => parseInt(p.trim())).filter(p => !isNaN(p))
          : Array.isArray(records.permissions)
          ? records.permissions
          : [];*/
          // 1️⃣ Get IDs
           //const permissionIds = this.parsePermissions(records.permissions);

           // Directly map IDs to objects
           this.formInputs.permissions = this.getPermissionList(records.permissions);
      },
      
   },
}; 
</script>